import styled from '@emotion/styled';
import React, { FC, useEffect, useState } from 'react';

import {
  Button,
  Callout,
  Input,
  FlexItem,
  FlexGroup,
  Text,
  Toggle,
  Loading,
  Badge,
  SplitPanelLayout,
  FlexGrid,
} from '@lithos';

import { useGetOnboardingStatus } from '../../../api/onboarding';
import { useGetInternalSparkClusterStatusQuery } from '../../../api/sparkClusterStatus';
import {
  USER_SETTINGS_RESPONSE,
  useGetDataPlatformSetupStatusQuery,
  useGetUserDeploymentSettingsQuery,
  useUpdateUserDeploymentSettingsMutation,
} from '../../../api/userDeploymentSettings';
import { updateObjectProperties } from '../../../utils/object-util';
import { useUserSettings } from '../../context/UserSettingsContext';
import {
  dataPlatformConfigFormSchema,
  getFieldMaskValues,
  mapDPOSubmission,
} from '../../data-platform-configuration/DPOUtils';
import { ErrorsInterface, SubmissionInterface } from '../../data-platform-configuration/types';

import { scrollIntoViewByElementId } from '../../../utils/scrollIntoViewCallback';
import {
  ConfigurationStatusType,
  sparkClusterStatusToHealthMap,
  sparkClusterStatusToResultMood,
} from '../../data-platform-configuration/DataPlatformTypes';
import { SPARK_STATUS } from './consts';
import DataPlatformHelpColumn from './DataPlatformHelpColumn';
import FormErrorFeedback from './FormErrorFeedback';
import FormProgressFeedback from './FormProgressFeedback';
import { ConfigurationCautionWrapper } from './StyledComponents';

// generated code
import { OnboardingStatusEnum } from '../../../types/tecton_proto/data/onboarding';
import {
  GetDataPlatformSetupStatusResponse,
  GetInternalSparkClusterStatusResponse,
} from '../../../types/tecton_proto/metadataservice/metadata_service';

import { InternalSparkClusterStatusEnum } from '../../../types/tecton_proto/data/internal_spark_cluster_status';

// @types
interface SparkStatusProps {
  data?: GetInternalSparkClusterStatusResponse;
  isLoading?: boolean;
  error?: boolean;
}

type SparkConfigProps = SparkStatusProps &
  USER_SETTINGS_RESPONSE & {
    setIsEditing: (isEditing: boolean) => void;
    feedback?: React.ReactElement;
    canDisableFormButton?: boolean;
  };

const defaultErrors = {
  submission: null,
  api_token: [],
  workspace_url: [],
  instance_profile_arn: [],
  glueCatalogId: [],
};

const INITIAL_DATA: SubmissionInterface = {
  workspace_url: '',
  api_token: '',
  instance_profile_arn: '',
  glueCatalogEnabled: false,
  glueCatalogId: '',
};

const DataPlatformWrapper = styled.div`
  font-weight: ${({ theme }) => theme.font.weight.medium};
  line-height: ${({ theme }) => theme.font.lineHeights.s};
`;

// Main Entry Component
// @DataPlatform Config Edit
const DataPlatformConfigEdit: FC = () => {
  const { isAdmin, workspace } = useUserSettings();
  //  State
  const [formData, setFormData] = useState(INITIAL_DATA);
  const [savedData, setSavedData] = useState(INITIAL_DATA);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [canDisableFormButton, setCanDisableFormButton] = useState(!isAdmin);
  const [isEditing, setIsEditing] = useState(false);
  const [onboardingReceived, setOnboardingReceived] = useState(false);
  const [formValidationErrors, setFormValidationErrors] = useState<ErrorsInterface>(defaultErrors);

  // DATA
  const { data: onboardingStatus, isLoading: onboardingStatusIsLoading } = useGetOnboardingStatus(workspace ?? 'prod');
  const { data: serverData, isLoading: serverLoading, error: serverError } = useGetUserDeploymentSettingsQuery();
  const { data: sparkData, isLoading: sparkLoading, error: sparkError } = useGetInternalSparkClusterStatusQuery(3000);
  const { mutate, error: UpdateError, data: updateData } = useUpdateUserDeploymentSettingsMutation();
  const SUBMISSION_ERROR = UpdateError?.message || updateData?.error_message;

  const {
    data: statusData,
    isLoading: statusLoading,
    error: statusError,
  } = useGetDataPlatformSetupStatusQuery(canDisableFormButton, 3000);

  const createFocusHandler = (entryId: string) => {
    return () => {
      scrollIntoViewByElementId(entryId);
    };
  };

  // Form Utils
  const schema = dataPlatformConfigFormSchema;

  // Update Form Properties
  const updateForm = (key: keyof SubmissionInterface, value: string | boolean) => {
    setFormData((prev) => {
      return updateObjectProperties(prev, { [key]: value });
    });
  };

  // Form Submission
  const submitForm = (e: any) => {
    e.preventDefault();
    const field_mask = getFieldMaskValues(formData, savedData);
    const submission = mapDPOSubmission(formData, field_mask);
    mutate(submission, {
      onSuccess: (data) => {
        if (data.success) {
          setCanDisableFormButton(true);
          setIsSubmitted(true);
          setIsEditing(false);
        }
      },
    });
  };

  // Cancel Form Editing
  const cancelForm = () => {
    setFormData(savedData);
    validateForm();
    setIsEditing(false);
  };

  // Form Validation - immediate validation for real-time feedback
  const validateForm = async (dataToValidate = formData) => {
    setFormValidationErrors(defaultErrors);
    try {
      await schema.validate(dataToValidate, { abortEarly: false });
    } catch (err: any) {
      err.inner.forEach((err: any) => {
        // Always set validation errors, regardless of whether field differs from saved data
        setFormValidationErrors((prev) => {
          return updateObjectProperties(prev, { [err.path]: err.errors });
        });
      });
    }
  };

  // UPDATE SAVED DATA FROM RESPONSE
  useEffect(() => {
    if (serverData) {
      const {
        workspace_url,
        api_token,
        instance_profile_arn,
        glueCatalogEnabled,
        glueCatalogId,
      }: USER_SETTINGS_RESPONSE = serverData || {};
      const dataMap = {
        workspace_url,
        api_token: api_token?.value || api_token?.encrypted_value || api_token?.redacted_value || '',
        instance_profile_arn,
        glueCatalogEnabled,
        glueCatalogId,
      };
      setFormData(dataMap);
      setSavedData(dataMap);
    }
  }, [serverData]);

  // Check For Onboarding
  useEffect(() => {
    if (onboardingStatus && !onboardingReceived) {
      const onboarding = onboardingStatus?.setup_platform === OnboardingStatusEnum.ONBOARDING_STATUS_INCOMPLETE;
      if (onboarding && !isEditing) {
        setIsEditing(true);
      }
      setOnboardingReceived(true);
    }
  }, [isEditing, onboardingReceived, onboardingStatus]);

  // Check SparkUpdateStatus
  useEffect(() => {
    if (canDisableFormButton && statusData?.setupCompleted) {
      setCanDisableFormButton(false);
    }
  }, [canDisableFormButton, statusData?.setupCompleted]);

  // FORM ERRORS
  const WORKSPACE_URL_ERROR =
    !!formValidationErrors?.workspace_url?.length && formData.workspace_url !== savedData.workspace_url;
  const API_TOKEN_ERROR = !!formValidationErrors?.api_token?.length && formData.api_token !== savedData.api_token;
  const INSTANCE_PROFILE_ERROR =
    !!formValidationErrors?.instance_profile_arn?.length &&
    formData.instance_profile_arn !== savedData.instance_profile_arn;
  const GLUE_ID_ERROR =
    !!formValidationErrors?.glueCatalogId?.length &&
    formData.glueCatalogId !== savedData.glueCatalogId &&
    formData.glueCatalogEnabled;
  const IS_FORM_ERROR = WORKSPACE_URL_ERROR || API_TOKEN_ERROR || INSTANCE_PROFILE_ERROR || GLUE_ID_ERROR;

  if (serverLoading || onboardingStatusIsLoading) {
    return <Loading size="l" />;
  }

  if (serverError) {
    return <div>there was an error loading your data.</div>;
  }

  return (
    <>
      <SplitPanelLayout
        left={
          !isEditing ? (
            <DataPlatformWrapper>
              <FlexGroup direction="column" gapSize="4">
                {isSubmitted ? (
                  <Feedback isSubmitted={isSubmitted} data={statusData} isLoading={statusLoading} error={statusError} />
                ) : (
                  <>
                    {sparkData?.status?.status ==
                    InternalSparkClusterStatusEnum.INTERNAL_SPARK_CLUSTER_STATUS_NOT_APPLICABLE ? (
                      <></>
                    ) : (
                      <Callout
                        variant={
                          sparkClusterStatusToResultMood[
                            sparkData?.status?.status ??
                              InternalSparkClusterStatusEnum.INTERNAL_SPARK_CLUSTER_STATUS_UNHEALTHY
                          ]
                        }
                        title="System Status"
                        content={<SparkStatus data={sparkData} isLoading={sparkLoading} error={!!sparkError} />}
                      />
                    )}
                  </>
                )}
                <DataPlatformConfig
                  {...serverData}
                  data={sparkData}
                  setIsEditing={setIsEditing}
                  canDisableFormButton={!isAdmin}
                />
              </FlexGroup>
            </DataPlatformWrapper>
          ) : (
            <>
              <FlexGroup gapSize="5" direction="column">
                <FlexItem>
                  <Text>
                    <p>
                      Update your Databricks cluster configurations. Note that you will need Databricks admin access to
                      get some of the required information.
                    </p>
                  </Text>
                </FlexItem>
                <FlexItem>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      submitForm(e);
                    }}
                  >
                    <FlexGroup gapSize="3" direction="column">
                      <FlexItem>
                        <Input
                          value={formData.workspace_url}
                          onChange={(newValue) => updateForm('workspace_url', newValue)}
                          type="string"
                          validationState={WORKSPACE_URL_ERROR ? 'error' : undefined}
                          label="Databricks Workspace URL"
                          focusCallback={createFocusHandler('docs-workspace-url')}
                          description={formValidationErrors.workspace_url}
                        />
                      </FlexItem>
                      <FlexItem>
                        <Input
                          value={formData.api_token}
                          onChange={(newValue) => updateForm('api_token', newValue)}
                          type="password"
                          label="Databricks API Token"
                          description={formValidationErrors.api_token}
                          validationState={API_TOKEN_ERROR ? 'error' : undefined}
                        />
                      </FlexItem>
                      <FlexItem>
                        {SUBMISSION_ERROR && (
                          <FormErrorFeedback
                            title="Form Submission Error"
                            errors={[
                              {
                                message: SUBMISSION_ERROR,
                              },
                            ]}
                          />
                        )}
                      </FlexItem>
                      <FlexItem>
                        <Input
                          label="Instance Profile"
                          value={formData.instance_profile_arn}
                          onChange={(newValue) => updateForm('instance_profile_arn', newValue)}
                          type="string"
                          validationState={INSTANCE_PROFILE_ERROR ? 'error' : undefined}
                          description={formValidationErrors.instance_profile_arn}
                        />
                      </FlexItem>
                      <FlexItem>
                        <Text size="xs" weight="medium">
                          AWS Glue Catalog Access
                        </Text>
                        <Toggle
                          label={`${formData.glueCatalogEnabled ? 'Enabled' : 'Disabled'}`}
                          state={!!formData.glueCatalogEnabled}
                          onClick={() => {
                            updateForm('glueCatalogEnabled', !formData.glueCatalogEnabled);
                            updateForm('glueCatalogId', '');
                          }}
                        />
                      </FlexItem>
                      <FlexItem>
                        {formData.glueCatalogEnabled && (
                          <Input
                            label="AWS Glue Catalog ID"
                            value={formData.glueCatalogId}
                            onChange={(newValue) => {
                              updateForm('glueCatalogId', newValue);
                              validateForm({ ...formData, glueCatalogId: newValue });
                            }}
                            type="string"
                            validationState={GLUE_ID_ERROR ? 'error' : undefined}
                            description={formValidationErrors.glueCatalogId}
                            focusCallback={createFocusHandler('docs-glue-catalog')}
                          />
                        )}
                      </FlexItem>
                    </FlexGroup>
                  </form>
                </FlexItem>
                <FlexItem>
                  <ConfigurationCautionWrapper>
                    Caution: Updating the Databricks configuration will cause any plan/apply to fail while the Tecton
                    Internal Spark Cluster restarts, and any running materialization jobs may be interrupted.
                  </ConfigurationCautionWrapper>
                </FlexItem>
                <FlexItem css={{ alignSelf: 'end' }}>
                  <FlexGroup direction="row" gapSize="1">
                    <FlexItem grow={0}>
                      <Button type="secondary" state="danger" onClick={() => cancelForm()} label={'Cancel'} />
                    </FlexItem>
                    <FlexItem grow={0}>
                      <Button
                        onClick={(e) => {
                          submitForm(e);
                        }}
                        label={'Update Databricks Configuration'}
                        type={'primary'}
                        state={IS_FORM_ERROR || canDisableFormButton ? 'disabled' : 'default'}
                      />
                    </FlexItem>
                  </FlexGroup>
                </FlexItem>
              </FlexGroup>
            </>
          )
        }
        right={
          <>
            <DataPlatformHelpColumn />
          </>
        }
      />
    </>
  );
};

export default DataPlatformConfigEdit;

// @Status Component
const SparkStatus = ({ data, isLoading, error }: SparkStatusProps) => {
  if (isLoading) return <Loading size="s" />;
  if (error) return <></>;
  const status =
    data?.status?.status != undefined
      ? ConfigurationStatusType[data?.status?.status as unknown as keyof typeof ConfigurationStatusType]
      : '';
  const { sparkClusterStatus } = SPARK_STATUS[status as unknown as keyof typeof SPARK_STATUS];
  const error_message = data?.status?.error_message || '';
  return (
    <Badge
      color={
        sparkClusterStatusToHealthMap[
          data?.status?.status ?? InternalSparkClusterStatusEnum.INTERNAL_SPARK_CLUSTER_STATUS_UNSPECIFIED
        ]
      }
      label={error_message ? error_message : sparkClusterStatus}
    />
  );
};

// @ PLATFORM CONFIG - READONLY
const DataPlatformConfig = ({
  workspace_url,
  api_token,
  instance_profile_arn,
  glueCatalogEnabled,
  glueCatalogId,
  setIsEditing,
  canDisableFormButton,
  data,
}: SparkConfigProps) => {
  const dataPlatformConfigList = [
    {
      title: 'Platform Type',
      description: (
        <>
          <div style={{ alignSelf: 'flex-start' }}>
            <Button
              label={'Go to Databricks'}
              type="secondary"
              iconRight={'ArrowUpRight'}
              onClick={() => {
                window.open(data?.status?.cluster_url, '_blank', 'rel=noopener noreferrer');
              }}
            />
          </div>
        </>
      ),
    },
    {
      title: 'Databricks Workspace URL',
      description: workspace_url ?? '',
    },
    {
      title: 'Databricks API Token',
      description: <>{api_token?.value || api_token?.encrypted_value || api_token?.redacted_value}</>,
    },
    {
      title: 'Instance Profile ARN',
      description: instance_profile_arn ?? '',
    },
    {
      title: 'AWS Glue Catalog Access',
      description: (
        <div>
          <Badge
            color={glueCatalogEnabled ? 'blue' : 'gray'}
            label={glueCatalogEnabled ? 'Enabled' : 'Disabled'}
            size="s"
          />
        </div>
      ),
    },
    {
      title: 'AWS Glue Catalog ID (Optional)',
      description: glueCatalogId ?? 'None',
    },
  ];

  return (
    <>
      <FlexItem>
        <FlexGrid columns={2} gapSize="4">
          {dataPlatformConfigList.map((item) => {
            return (
              <>
                <FlexItem>{item.title}</FlexItem>
                <FlexItem grow={0}>{item.description}</FlexItem>
              </>
            );
          })}
        </FlexGrid>
      </FlexItem>
      <FlexItem css={{ alignSelf: 'end' }}>
        <Button
          state={canDisableFormButton ? 'disabled' : 'default'}
          type="secondary"
          onClick={() => {
            setIsEditing(true);
          }}
          label="Edit Databricks Configuration"
          iconRight={'Edit'}
        />
      </FlexItem>
    </>
  );
};

const Feedback = ({
  isSubmitted,
  data,
  isLoading,
  error,
}: {
  isSubmitted: boolean;
  data: GetDataPlatformSetupStatusResponse | undefined;
  isLoading: boolean;
  error: any;
}) => {
  const setupCompleted = data?.setupCompleted;

  if (isLoading && isSubmitted) {
    return <Loading size="l" />;
  }

  if (error) {
    return (
      <FormErrorFeedback
        title="Databricks Configuration Error"
        errors={[
          {
            title: 'We encountered an unexpected error.',
            message: 'This may be transient, try refreshing the browser',
          },
        ]}
      />
    );
  } else if (isSubmitted) {
    return <FormProgressFeedback setupCompleted={setupCompleted} />;
  }

  return <></>;
};
